import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { toast } from 'sonner';
import { QueryState } from '../components/page';
import {
  useDeleteFeatureOverride,
  useFeatureOverridesFor,
  useFeatures,
  useTenantMembers,
  useUpsertFeatureOverride,
} from '../api/hooks';
import type { FeatureOverrideRow, FeatureRegistryEntry } from '../api/types';

type TenantState = 'inherit' | 'on' | 'off';

/** The value a tenant member gets before role and user overrides apply. */
function tenantValue(feature: FeatureRegistryEntry, override: FeatureOverrideRow | undefined) {
  if (override) return { enabled: override.enabled, source: 'tenant' };
  if (feature.platform_override !== null)
    return { enabled: feature.platform_override, source: 'environment' };
  return { enabled: feature.default_enabled, source: 'default' };
}

/**
 * Tenant-wide feature flags. Rudi resolves each user as user > role > tenant >
 * legacy tenant setting > environment > default, so a value set here applies
 * to every member unless a role or user override says otherwise.
 */
export function TenantFeaturesSection({ tenantId }: { tenantId: string }) {
  const features = useFeatures();
  const keys = features.data?.features.map(feature => feature.key) ?? [];
  const overrides = useFeatureOverridesFor(keys);
  const members = useTenantMembers(tenantId);
  const memberIds = new Set(members.data?.members.map(member => member.user_id) ?? []);
  const tenantRoles = new Set(
    (features.data?.roles ?? []).filter(role => role.tenant_id === tenantId).map(role => role.id)
  );
  const upsert = useUpsertFeatureOverride();
  const remove = useDeleteFeatureOverride();
  const busy = upsert.isPending || remove.isPending;

  const change = async (
    feature: FeatureRegistryEntry,
    row: FeatureOverrideRow | undefined,
    value: TenantState
  ) => {
    try {
      if (value === 'inherit') {
        if (row) await remove.mutateAsync({ key: feature.key, overrideId: row.id });
      } else {
        await upsert.mutateAsync({
          key: feature.key,
          subject_type: 'tenant',
          subject_id: tenantId,
          enabled: value === 'on',
        });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <section className="mt-6 space-y-3">
      <div>
        <h2 className="text-sm font-medium">Feature flags</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Applies to every member of this tenant. Role and user overrides still win, and each
          user&apos;s Applications tab shows the value they end up with. Inherit shows the
          environment value; an old tenant setting from before overrides existed can still apply and
          is not listed here, so choose Force ON or OFF to be certain. Rudi users who are signed in
          see a change after they reload the page.
        </p>
      </div>
      <QueryState
        isLoading={features.isLoading || overrides.some(query => query.isLoading)}
        error={features.error ?? overrides.find(query => query.error)?.error ?? null}
        empty={keys.length === 0}
      >
        <div className="overflow-hidden rounded-3xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Feature</TableHead>
                <TableHead>Tenant value</TableHead>
                <TableHead>Tenant override</TableHead>
                <TableHead>Narrower overrides</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.data?.features.map((feature, index) => {
                const rows = overrides[index]?.data?.overrides ?? [];
                const row = rows.find(
                  item => item.subject_type === 'tenant' && item.subject_id === tenantId
                );
                const value = tenantValue(feature, row);
                const roleCount = rows.filter(
                  item => item.subject_type === 'role' && tenantRoles.has(item.subject_id)
                ).length;
                const userCount = rows.filter(
                  item => item.subject_type === 'user' && memberIds.has(item.subject_id)
                ).length;
                const state: TenantState = row ? (row.enabled ? 'on' : 'off') : 'inherit';
                return (
                  <TableRow key={feature.key}>
                    <TableCell>
                      <span className="font-medium">{feature.label}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {feature.key}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={value.enabled ? 'default' : 'secondary'}>
                        {value.enabled ? 'ON' : 'OFF'}
                      </Badge>
                      <span className="ml-2 text-xs text-muted-foreground">{value.source}</span>
                    </TableCell>
                    <TableCell>
                      <select
                        aria-label={`Tenant override for ${feature.label}`}
                        className="h-9 w-36 rounded-3xl border border-input bg-background px-3 text-sm"
                        value={state}
                        disabled={busy}
                        onChange={event =>
                          void change(feature, row, event.target.value as TenantState)
                        }
                      >
                        <option value="inherit">Inherit</option>
                        <option value="on">Force ON</option>
                        <option value="off">Force OFF</option>
                      </select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {roleCount} role · {userCount} user
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </section>
  );
}
